import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import OrganizerLayout from '../../components/layouts/OrganizerLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Check, X, Crown, Shield, Sparkles, ArrowLeft, Loader2,
  ShoppingCart, Building2, Award, Zap, Users, FileText,
  CreditCard, Calendar, Megaphone, ClipboardList, UserCheck,
  HelpCircle, Lock
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for dynamic icons from backend
const iconMap = {
  Calendar: Calendar,
  Users: Users,
  Building2: Building2,
  CreditCard: CreditCard,
  Megaphone: Megaphone,
  FileText: FileText,
  ClipboardList: ClipboardList,
  UserCheck: UserCheck,
  HelpCircle: HelpCircle,
};

const SubscriptionPlans = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [featureComparison, setFeatureComparison] = useState([]);
  const [addingToCart, setAddingToCart] = useState(null);
  const [cartCount, setCartCount] = useState(0);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  
  // Check if redirected from event creation
  const fromEventCreation = location.state?.fromEventCreation;
  const newEventCode = location.state?.eventCode;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [plansRes, matrixRes] = await Promise.all([
        axios.get(`${API_URL}/api/subscription-plans/public`),
        axios.get(`${API_URL}/api/feature-matrix/public`)
      ]);
      setSubscriptionPlans(plansRes.data);
      setFeatureComparison(matrixRes.data.categories || []);
      
      if (token) {
        try {
          const cartRes = await axios.get(`${API_URL}/api/organizer/cart`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setCartCount(cartRes.data.items?.length || 0);
        } catch (e) {}
        
        // Get current subscription
        try {
          const subRes = await axios.get(`${API_URL}/api/organizer/subscription`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setCurrentSubscription(subRes.data);
        } catch (e) {}
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (plan) => {
    if (plan.price === 0) {
      toast.info('Basic plan is free! You already have access.');
      navigate('/organizer');
      return;
    }

    if (!token) {
      toast.error('Please login to add to cart');
      navigate('/organizer/login');
      return;
    }

    setAddingToCart(plan.id);
    try {
      await axios.post(`${API_URL}/api/organizer/cart/add?plan_id=${plan.id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${plan.name} added to cart`);
      setCartCount(prev => prev + 1);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to cart');
    } finally {
      setAddingToCart(null);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === 0) return 'FREE';
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getPlanIcon = (tier) => {
    switch (tier) {
      case 'basic': return Zap;
      case 'organized': return Award;
      case 'transparency': return Shield;
      case 'license': return Crown;
      default: return Sparkles;
    }
  };

  const getPlanColor = (color) => {
    const colors = {
      green: 'border-green-500 bg-green-50',
      blue: 'border-blue-500 bg-blue-50',
      purple: 'border-purple-500 bg-purple-50',
      orange: 'border-orange-500 bg-orange-50'
    };
    return colors[color] || 'border-gray-200';
  };

  const renderFeatureValue = (value) => {
    if (value === true) {
      return <Check className="h-5 w-5 text-green-600 mx-auto" />;
    }
    if (value === false) {
      return <X className="h-5 w-5 text-gray-300 mx-auto" />;
    }
    return <span className="text-sm font-medium text-gray-700">{value}</span>;
  };

  if (loading) {
    return (
      <OrganizerLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-maroon" />
        </div>
      </OrganizerLayout>
    );
  }

  return (
    <OrganizerLayout>
      <div className="space-y-8 animate-fade-in pb-20" data-testid="subscription-plans-page">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto">
          {fromEventCreation && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
              <div className="flex items-center justify-center gap-2 text-green-700">
                <Check className="h-5 w-5" />
                <span className="font-medium">Event Created Successfully!</span>
              </div>
              {newEventCode && (
                <p className="text-sm text-green-600 mt-1">
                  Your event code is: <span className="font-mono font-bold">{newEventCode}</span>
                </p>
              )}
              <p className="text-sm text-green-600 mt-2">
                Choose a subscription plan to unlock all features for your reunion.
              </p>
            </div>
          )}
          
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-maroon">
            Choose Your Reunion Plan
          </h1>
          <p className="text-gray-600 mt-3 text-lg">
            Select the perfect plan for your batch's reunion needs
          </p>
          
          {cartCount > 0 && (
            <Button
              variant="outline"
              className="mt-4 border-maroon text-maroon"
              onClick={() => navigate('/organizer/cart')}
            >
              <ShoppingCart className="h-4 w-4 mr-2" />
              View Cart ({cartCount})
            </Button>
          )}
        </div>

        {/* Plan Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {subscriptionPlans.map((plan) => {
            const PlanIcon = getPlanIcon(plan.tier);
            const isCurrentPlan = currentSubscription?.tier === plan.tier;
            
            return (
              <Card 
                key={plan.id}
                className={`relative border-2 transition-all hover:shadow-lg ${
                  plan.is_popular ? 'border-blue-500 shadow-blue-100' : 'border-gray-200'
                } ${isCurrentPlan ? 'ring-2 ring-maroon' : ''}`}
                data-testid={`plan-card-${plan.tier}`}
              >
                {plan.is_popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-blue-500 text-white">Most Popular</Badge>
                  </div>
                )}
                {isCurrentPlan && (
                  <div className="absolute -top-3 right-4">
                    <Badge className="bg-maroon text-white">Current Plan</Badge>
                  </div>
                )}
                
                <CardHeader className="text-center pb-2">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3 ${getPlanColor(plan.color)}`}>
                    <PlanIcon className="h-7 w-7" style={{ color: plan.color === 'green' ? '#22c55e' : plan.color === 'blue' ? '#3b82f6' : plan.color === 'purple' ? '#a855f7' : '#f97316' }} />
                  </div>
                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                  <CardDescription className="text-sm">{plan.tagline}</CardDescription>
                </CardHeader>
                
                <CardContent className="text-center space-y-4">
                  <div>
                    <span className="text-3xl font-bold text-gray-900">{formatCurrency(plan.price)}</span>
                    {plan.price > 0 && (
                      <span className="text-gray-500 text-sm ml-1">
                        /{plan.price_type === 'yearly' ? 'year' : 'event'}
                      </span>
                    )}
                  </div>
                  
                  <div className="text-sm text-gray-600 space-y-1">
                    <p>{plan.max_members === -1 ? 'Unlimited' : plan.max_members} attendees</p>
                    <p>{plan.max_events === -1 ? 'Unlimited' : plan.max_events} event{plan.max_events !== 1 ? 's' : ''}</p>
                  </div>
                  
                  <Button
                    className={`w-full ${
                      plan.is_popular 
                        ? 'bg-blue-500 hover:bg-blue-600' 
                        : plan.price === 0 
                          ? 'bg-gray-500 hover:bg-gray-600'
                          : 'bg-maroon hover:bg-maroon-hover'
                    }`}
                    onClick={() => handleAddToCart(plan)}
                    disabled={addingToCart === plan.id || isCurrentPlan}
                    data-testid={`select-plan-${plan.tier}`}
                  >
                    {addingToCart === plan.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : isCurrentPlan ? (
                      'Current Plan'
                    ) : plan.price === 0 ? (
                      'Get Started Free'
                    ) : (
                      <>
                        <ShoppingCart className="h-4 w-4 mr-2" />
                        Add to Cart
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Feature Comparison Table */}
        <div className="mt-12">
          <h2 className="font-heading text-2xl font-bold text-center text-gray-900 mb-6">
            Compare All Features
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full border-collapse" data-testid="feature-comparison-table">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-4 px-4 font-semibold text-gray-700 min-w-[200px]">Feature</th>
                  <th className="text-center py-4 px-3 font-semibold text-green-600 min-w-[100px]">
                    <div className="flex flex-col items-center">
                      <Zap className="h-5 w-5 mb-1" />
                      Basic
                    </div>
                  </th>
                  <th className="text-center py-4 px-3 font-semibold text-blue-600 min-w-[100px]">
                    <div className="flex flex-col items-center">
                      <Award className="h-5 w-5 mb-1" />
                      Organized
                    </div>
                  </th>
                  <th className="text-center py-4 px-3 font-semibold text-purple-600 min-w-[100px]">
                    <div className="flex flex-col items-center">
                      <Shield className="h-5 w-5 mb-1" />
                      Transparency
                    </div>
                  </th>
                  <th className="text-center py-4 px-3 font-semibold text-orange-600 min-w-[100px]">
                    <div className="flex flex-col items-center">
                      <Crown className="h-5 w-5 mb-1" />
                      License
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {featureComparison.map((category, catIdx) => {
                  const IconComponent = iconMap[category.icon] || Calendar;
                  return (
                  <React.Fragment key={category.category}>
                    {/* Category Header */}
                    <tr className="bg-gray-50">
                      <td colSpan={5} className="py-3 px-4">
                        <div className="flex items-center gap-2 font-semibold text-gray-800">
                          <IconComponent className="h-4 w-4 text-maroon" />
                          {category.category}
                        </div>
                      </td>
                    </tr>
                    {/* Features */}
                    {category.features.map((feature, featIdx) => (
                      <tr 
                        key={feature.name}
                        className={`border-b border-gray-100 ${featIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                      >
                        <td className="py-3 px-4 text-gray-700">{feature.name}</td>
                        <td className="py-3 px-3 text-center">{renderFeatureValue(feature.basic)}</td>
                        <td className="py-3 px-3 text-center">{renderFeatureValue(feature.organized)}</td>
                        <td className="py-3 px-3 text-center">{renderFeatureValue(feature.transparency)}</td>
                        <td className="py-3 px-3 text-center">{renderFeatureValue(feature.license)}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center mt-12 p-8 bg-gradient-to-r from-maroon/5 to-maroon/10 rounded-2xl">
          <h3 className="text-xl font-bold text-gray-900 mb-2">Need help choosing?</h3>
          <p className="text-gray-600 mb-4">
            Most batches choose <span className="font-semibold text-blue-600">Organized Reunion</span> - 
            it has everything you need for a successful reunion!
          </p>
          <Button 
            variant="outline" 
            className="border-maroon text-maroon"
            onClick={() => navigate('/organizer/help')}
          >
            <HelpCircle className="h-4 w-4 mr-2" />
            View FAQ
          </Button>
        </div>
      </div>
    </OrganizerLayout>
  );
};

export default SubscriptionPlans;
